import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, IconButton, Tooltip, CircularProgress } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Refresh } from "iconsax-react";
import { authGet } from "./routingApi";
import { PALETTE, formatDuration } from "../callsPalette";

const STATE_META = {
  free: { label: "Free", color: PALETTE.good },
  on_call: { label: "On a call", color: PALETTE.serious },
  ringing: { label: "Being rung", color: PALETTE.blue },
  offline: { label: "Offline", color: PALETTE.muted },
};
const REFRESH_MS = 10000;

function StatePill({ state }) {
  const meta = STATE_META[state] || STATE_META.offline;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1,
        py: 0.25,
        borderRadius: 10,
        fontSize: 12,
        fontWeight: 600,
        color: meta.color.fill || meta.color.ring,
        bgcolor: alpha(meta.color.ring, 0.14),
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: meta.color.ring }} />
      {meta.label}
    </Box>
  );
}

const idleText = (a) => {
  if (!a.lastCallEndedAt) return "No calls in 24h";
  const secs = (Date.now() - new Date(a.lastCallEndedAt).getTime()) / 1000;
  return `Idle ${secs < 60 ? "<1m" : formatDuration(Math.round(secs / 60) * 60)}`;
};

/**
 * "If a call came in right now…" — the same availability logic the live engine uses, fetched from
 * the server and re-fetched as the admin edits (unsaved choices are sent as query params), so the
 * predicted order is never a guess. Polls only while the tab is visible.
 */
export default function LiveRingPreview({ draft, onAgentCount }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const reqId = useRef(0);
  const onAgentCountRef = useRef(onAgentCount);
  onAgentCountRef.current = onAgentCount;

  const { strategy, mode, activeRingGroupId, skipBusyAgents } = draft;
  const fetchPreview = useCallback(async () => {
    const id = ++reqId.current;
    try {
      const res = await authGet("/api/calling/routing/preview", { strategy, mode, activeRingGroupId: activeRingGroupId || undefined, skipBusyAgents });
      if (id !== reqId.current) return; // a newer request superseded this one
      setData(res);
      setFailed(false);
      onAgentCountRef.current?.(res.agents || []);
    } catch (err) {
      if (id === reqId.current) setFailed(true);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [strategy, mode, activeRingGroupId, skipBusyAgents]);

  useEffect(() => {
    const t = setTimeout(fetchPreview, 250); // debounce rapid edits
    const poll = setInterval(() => {
      if (document.visibilityState === "visible") fetchPreview();
    }, REFRESH_MS);
    return () => {
      clearTimeout(t);
      clearInterval(poll);
    };
  }, [fetchPreview]);

  const agents = data?.agents || [];
  const staged = strategy !== "simultaneous";

  return (
    <Box sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2, overflow: "hidden", bgcolor: "background.paper" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25, borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>If a call came in right now</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {staged ? "Predicted ring order · live, refreshes every 10s" : "These agents would ring together · live, refreshes every 10s"}
          </Typography>
        </Box>
        {loading ? (
          <CircularProgress size={16} />
        ) : (
          <Tooltip title="Refresh now">
            <IconButton size="small" onClick={fetchPreview} aria-label="Refresh preview">
              <Refresh size={16} color="currentColor" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {failed && !data && (
        <Typography sx={{ p: 2, fontSize: 13, color: "text.secondary" }}>Couldn't load the live preview. It will retry automatically.</Typography>
      )}
      {!failed && !loading && agents.length === 0 && (
        <Typography sx={{ p: 2, fontSize: 13, color: "text.secondary" }}>
          Nobody would be rung. Agents need to be online and have the Calls → Add permission
          {mode === "ring_group" ? ", and the ring group needs at least one member." : "."}
        </Typography>
      )}

      {agents.map((a, i) => {
        const skipped = staged && !a.willRing;
        return (
          <Box
            key={a.userId}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2,
              py: 1,
              borderTop: i === 0 ? "none" : (t) => `1px solid ${t.palette.divider}`,
              opacity: skipped ? 0.55 : 1,
            }}
          >
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 700,
                bgcolor: staged && a.willRing ? PALETTE.blue.fill : "action.selected",
                color: staged && a.willRing ? "#fff" : "text.secondary",
              }}
            >
              {staged ? (a.willRing ? a.position : "–") : "•"}
            </Box>
            <Typography sx={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 500 }} noWrap>
              {a.name}
            </Typography>
            {strategy === "smart" && a.willRing && (
              <Typography sx={{ fontSize: 12, color: "text.secondary", display: { xs: "none", sm: "block" } }}>{idleText(a)}</Typography>
            )}
            {skipped && <Typography sx={{ fontSize: 12, color: "text.secondary" }}>skipped</Typography>}
            <StatePill state={a.state} />
          </Box>
        );
      })}
    </Box>
  );
}
